<template>
  <div
    class="assistente-dock"
    :class="[`assistente-dock--${forma}`, { 'assistente-dock--com-suporte': suporteVisivel }]"
    :data-forma="forma"
    data-assistente-ignorar
    @keydown.esc.stop.prevent="aoPressionarEscape"
  >
    <button
      v-if="forma === 'minimizado' && larguraDaJanela >= 1024"
      type="button"
      class="assistente-dock__acionador"
      data-testid="assistente-dock-acionador"
      :aria-label="t('assistentePontotel.titulo')"
      @click="mudarIntencao('acionar')"
    >
      <span class="assistente-dock__rotulo"><span class="assistente-dock__acionador-texto">{{ t('assistentePontotel.titulo') }}</span></span>
      <span class="assistente-dock__foto">
        <img :src="fotoNina" alt="" width="56" height="56">
        <span class="assistente-dock__selo" aria-hidden="true"><span class="assistente-dock__estrela" /></span>
      </span>
    </button>

    <form
      v-else-if="forma === 'recolhido' && larguraDaJanela >= 1024"
      class="assistente-dock__pergunta"
      data-testid="assistente-dock-pergunta"
      @submit.prevent="enviarPerguntaRecolhida"
    >
      <!--
        Sem isto a pílula é rua de mão única: dá para perguntar de novo, mas
        não para voltar ao que já foi conversado. O gesto `expandir` sempre
        existiu no domínio; faltava quem o disparasse.
      -->
      <button
        type="button"
        class="assistente-dock__expandir"
        data-testid="assistente-dock-expandir"
        :aria-label="t('assistentePontotel.acoes.abrirConversa')"
        :title="t('assistentePontotel.acoes.abrirConversa')"
        @click="mudarIntencao('expandir')"
      >
        <span class="assistente-dock__estrela" aria-hidden="true" />
      </button>
      <input
        v-model="perguntaRecolhida"
        data-testid="assistente-dock-input"
        type="text"
        :aria-label="t('assistentePontotel.composer.ariaLabel')"
        :placeholder="t('assistentePontotel.composer.placeholder')"
        :disabled="props.loading === true || selecaoPendente"
      >
      <button
        type="submit"
        data-testid="assistente-dock-enviar"
        :aria-label="t('assistentePontotel.composer.enviar')"
        :disabled="props.loading === true || selecaoPendente || !perguntaRecolhida.trim()"
      >
        <span aria-hidden="true">↑</span>
      </button>
      <button
        type="button"
        class="assistente-dock__fechar-pergunta"
        :aria-label="t('assistentePontotel.acoes.fechar')"
        @click="mudarIntencao('fechar')"
      >
        <span aria-hidden="true">×</span>
      </button>
    </form>

    <section
      v-if="conversaMontada"
      v-show="forma === 'acoplado' || forma === 'sobreposto'"
      ref="painelDaConversa"
      tabindex="-1"
      class="assistente-dock__painel"
      :class="{ 'assistente-dock__painel--abas': props.conversaEmAbas }"
      :aria-label="t('assistentePontotel.titulo')"
    >
      <!--
        N6 — o histórico mora aqui, e não no cabeçalho do canvas.
        Histórico de conversa é assunto da CONVERSA; hospedá-lo no canvas
        obrigava o canvas a existir para se chegar nele, e foi o que me fez
        (G6) manter 72px de cabeçalho vivo só para não perder a porta. Com a
        porta aqui, o canvas vazio pode colapsar de verdade.
      -->
      <header class="assistente-dock__cabecalho">
        <span class="assistente-dock__identidade">
          <span class="assistente-dock__estrela" aria-hidden="true" />
          <span class="assistente-dock__titulo">{{ t('assistentePontotel.titulo') }}</span>
        </span>
        <div class="assistente-dock__acoes">
          <!--
            "Nova conversa" vira ícone aqui, ao lado do histórico: as três
            ações da conversa — começar outra, rever as anteriores, fechar —
            passam a morar no mesmo lugar, e a coluna recupera a linha que o
            botão de texto ocupava. O de texto continua existindo na PÁGINA
            cheia, onde há largura de sobra e nenhum cabeçalho de dock.

            Antes dele, as opções avançadas — só quando o agente manda exibir.
          -->
          <OpcoesAvancadas
            v-if="props.opcoesAvancadas?.exibir === true"
            :opcoes="props.opcoesAvancadas"
            @update:modo-de-acao="emit('update:modo-de-acao', $event)"
          />
          <PontotelBotaoIcone
            data-testid="assistente-dock-nova-conversa"
            tamanho="P"
            tipo="neutro"
            peso="intermediario"
            :desabilitado="props.loading === true"
            :aria-label="t('assistentePontotel.acoes.novaConversa')"
            :title="t('assistentePontotel.acoes.novaConversa')"
            @clique="emit('nova-conversa')"
          >
            <template #icone>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </template>
          </PontotelBotaoIcone>
          <PontotelBotaoIcone
            data-testid="assistente-dock-historico"
            tamanho="P"
            tipo="neutro"
            peso="intermediario"
            :aria-label="t('assistentePontotel.historico.titulo')"
            :title="t('assistentePontotel.historico.titulo')"
            @clique="emit('history')"
          >
            <template #icone>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 6h11M9 12h11M9 18h11M4 6h1M4 12h1M4 18h1" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </template>
          </PontotelBotaoIcone>
          <PontotelBotaoIcone
            v-if="props.context?.role === 'gestor' || props.context?.role === 'rh'"
            data-testid="assistente-dock-politicas"
            tamanho="P"
            tipo="neutro"
            peso="intermediario"
            :aria-label="t('assistentePontotel.politicasDeIa.abrir')"
            :title="t('assistentePontotel.politicasDeIa.abrir')"
            @clique="emit('politicas')"
          >
            <template #icone>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v14H6.5A2.5 2.5 0 0 0 4 19.5v-14Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
                <path d="M8.5 8h6" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </template>
          </PontotelBotaoIcone>
          <PontotelBotaoIcone
            data-testid="assistente-dock-fechar"
            tamanho="P"
            tipo="neutro"
            peso="intermediario"
            :aria-label="t('assistentePontotel.acoes.fechar')"
            :title="t('assistentePontotel.acoes.fechar')"
            @clique="mudarIntencao('fechar')"
          >
            <template #icone>
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              </svg>
            </template>
          </PontotelBotaoIcone>
        </div>
      </header>

      <slot name="abas" />

      <div
        v-show="!props.conversaOculta"
        :id="props.conversaEmAbas ? 'resultado-conversa' : undefined"
        :role="props.conversaEmAbas ? 'tabpanel' : undefined"
        :aria-labelledby="props.conversaEmAbas ? 'aba-conversa' : undefined"
        :inert="props.conversaOculta"
        class="assistente-dock__conteudo"
      >
        <ConversationPane
          :messages="mensagens"
          :resultados-por-id="props.resultadosPorId"
          :resultado-ativo-id="props.resultadoAtivoId"
          :resultado-visivel="props.resultadoVisivel"
          :context="contexto"
          :loading="props.loading === true"
          :compacto="true"
          :leitura-da-tela-autorizada="props.leituraDaTelaAutorizada === true"
          @send="despacharEnvio"
          @nova-conversa="emit('nova-conversa')"
          @preview="emit('preview', $event)"
          @abrir-resultado="emit('abrir-resultado', $event)"
          @abrir-navegacao="emit('abrir-navegacao', $event)"
          @aceitar-oferta-de-suporte="emit('aceitar-oferta-de-suporte', $event)"
          @selecionar-opcao="emit('selecionar-opcao', $event)"
          @responder-solicitacao-de-parametros="emit('responder-solicitacao-de-parametros', $event)"
          @avaliar-resposta="emit('avaliar-resposta', $event)"
          @executar-planejamento="emit('executar-planejamento', $event)"
        />
      </div>
    </section>

    <p class="assistente-dock__anuncio" aria-live="polite">{{ anuncioDaForma }}</p>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  defineAsyncComponent,
  defineComponent,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from 'vue'
import { useI18n } from 'vue-i18n'
import { PontotelBotaoIcone } from '@Lar21/imaginii-web-components/lib/vue'
import { observarVisibilidadeDoCrisp } from '@/typescript/servicos/crisp/visibilidadeDoCrisp'
import fotoNina from '@/../static/images/nina.png'
import {
  CapacidadeAssistente,
  ContextoDeAtuacao,
  EscolhaDaSelecaoDaAntecipacao,
  MensagemAssistente,
  OpcoesAvancadas as OpcoesAvancadasDaConversa,
  PreviaDeMutacao,
  RespostaDaSolicitacaoDeParametros,
  ResultadoDeTurno,
  VotoDaAvaliacao
} from '../../dominio/assistente.interface'
import OpcoesAvancadas from './OpcoesAvancadas.vue'
import type { ModoDeAcao } from './opcoesAvancadas'
import { observarTela, registrarColheitaDeContextoDeTela } from '../canvas/observadorDeTela'
import {
  formaDoAcoplamento,
  lerIntencaoDoAcoplamento,
  persistirIntencaoDoAcoplamento,
  proximaIntencao
} from './acoplamentoDoAssistente'
import type {
  FormaDoAcoplamento,
  GestoDoAcoplamento,
  IntencaoDoAcoplamento
} from './acoplamentoDoAssistente'

const props = defineProps<{
  montado?: boolean
  intencao?: IntencaoDoAcoplamento
  larguraDaJanela?: number
  messages?: MensagemAssistente[]
  resultadosPorId?: Record<string, ResultadoDeTurno>
  resultadoAtivoId?: string | null
  resultadoVisivel?: boolean
  conversaEmAbas?: boolean
  conversaOculta?: boolean
  context?: ContextoDeAtuacao
  loading?: boolean
  /**
   * A pessoa autorizou a leitura dos dados da tela hospedeira. O dock só
   * repassa: quem guarda a decisão é a store, e quem a exibe é a conversa.
   */
  leituraDaTelaAutorizada?: boolean
  /** O menu de opções avançadas: o agente diz se aparece, a pessoa escolhe os modos. */
  opcoesAvancadas?: OpcoesAvancadasDaConversa
}>()

const emit = defineEmits<{
  send: [texto: string]
  'update:intencao': [intencao: IntencaoDoAcoplamento]
  'nova-conversa': []
  preview: [payload: { capability: CapacidadeAssistente, input: Record<string, string>, interacaoId?: string } | PreviaDeMutacao]
  'abrir-resultado': [id: string]
  'abrir-navegacao': [id: string]
  'aceitar-oferta-de-suporte': [payload: { id: string, resumo: string }]
  'selecionar-opcao': [escolha: EscolhaDaSelecaoDaAntecipacao]
  'responder-solicitacao-de-parametros': [resposta: RespostaDaSolicitacaoDeParametros]
  'avaliar-resposta': [payload: { interacaoId: string, voto: VotoDaAvaliacao, comentario?: string }]
  'executar-planejamento': [payload: { planejamentoId: string, versao: number }]
  history: []
  politicas: []
  'update:modo-de-acao': [payload: { modo: ModoDeAcao, ligado: boolean }]
}>()

const { t } = useI18n()
const suporteVisivel = ref(false)
let pararDeObservarSuporte: (() => void) | undefined

const contextoPadrao: ContextoDeAtuacao = {
  userId: 'assistente-dock',
  displayName: 'Usuário Pontotel',
  role: 'gestor',
  companyId: 'empresa-pontotel',
  companyName: 'Pontotel',
  scope: {}
}

const SELETOR_DE_EXCLUSAO_DA_COLHEITA = '[data-assistente-ignorar]'

function colherTelaHospedeira () {
  return observarTela(window, { ignorar: SELETOR_DE_EXCLUSAO_DA_COLHEITA })
}

const intencaoLocal = ref<IntencaoDoAcoplamento>(
  props.intencao ?? lerIntencaoDoAcoplamento()
)
const perguntaRecolhida = ref('')

function intencaoValida (valor: unknown): valor is IntencaoDoAcoplamento {
  return valor === 'minimizado' || valor === 'recolhido' || valor === 'aberto'
}

watch(() => props.intencao, (novaIntencao) => {
  if (intencaoValida(novaIntencao)) intencaoLocal.value = novaIntencao
})

const larguraDaJanela = ref(
  typeof props.larguraDaJanela === 'number'
    ? props.larguraDaJanela
    : typeof window === 'undefined' ? 0 : window.innerWidth
)

function atualizarLarguraDaJanela (): void {
  if (typeof props.larguraDaJanela === 'number') {
    larguraDaJanela.value = props.larguraDaJanela
    return
  }
  if (typeof window !== 'undefined') larguraDaJanela.value = window.innerWidth
}

watch(() => props.larguraDaJanela, atualizarLarguraDaJanela)

const forma = computed<FormaDoAcoplamento>(() => formaDoAcoplamento({
  intencao: intencaoLocal.value,
  larguraDaJanela: larguraDaJanela.value,
  montado: props.montado !== false,
  resultadoAberto: props.resultadoVisivel === true
}))

const painelDaConversa = ref<HTMLElement | null>(null)
const conversaMontada = ref(false)
watch(forma, async (atual, anterior) => {
  if (atual !== 'acoplado' && atual !== 'sobreposto') return
  conversaMontada.value = true
  if (anterior !== undefined && anterior !== 'acoplado' && anterior !== 'sobreposto') {
    await nextTick()
    painelDaConversa.value?.focus()
  }
}, { immediate: true })

const mensagens = computed(() => props.messages ?? [])
const selecaoPendente = computed(() => mensagens.value.some(mensagem => (
  mensagem.selecao?.estado === 'pendente' ||
  mensagem.selecao?.estado === 'enviando' ||
  mensagem.selecao?.estado === 'falhou'
)))
const contexto = computed(() => props.context ?? contextoPadrao)
// Carrega a conversa quando a pessoa abre o chat.
const ConversationPane = defineAsyncComponent({
  loader: () => import('../conversa/ConversationPane.vue'),
  errorComponent: defineComponent({
    name: 'ConversationPaneDegradado',
    setup: () => () => h('section', { class: 'conversa' })
  })
})

function mudarIntencao (gesto: GestoDoAcoplamento): void {
  const novaIntencao = proximaIntencao(intencaoLocal.value, gesto)
  if (novaIntencao === intencaoLocal.value) return
  intencaoLocal.value = novaIntencao
  persistirIntencaoDoAcoplamento(novaIntencao)
  emit('update:intencao', novaIntencao)
}

function despacharEnvio (texto: string): void {
  const pergunta = texto.trim()
  if (!pergunta || selecaoPendente.value) return
  emit('send', pergunta)
}

function enviarPerguntaRecolhida (): void {
  const pergunta = perguntaRecolhida.value.trim()
  if (!pergunta || props.loading === true || selecaoPendente.value) return
  despacharEnvio(pergunta)
  perguntaRecolhida.value = ''
  mudarIntencao('enviar')
}

function aoPressionarEscape (): void {
  if (forma.value === 'sobreposto') mudarIntencao('escapar')
}

function aoRedimensionar (): void {
  atualizarLarguraDaJanela()
  // Recalcular a forma nunca despacha uma transição de intenção.
  mudarIntencao('redimensionar')
}

const anuncioDaForma = computed(() => {
  if (forma.value !== 'acoplado' && forma.value !== 'sobreposto') return ''
  return t('assistentePontotel.canvas.titulo')
})

onMounted(() => {
  atualizarLarguraDaJanela()
  pararDeObservarSuporte = observarVisibilidadeDoCrisp(visivel => { suporteVisivel.value = visivel })
  window.addEventListener('resize', aoRedimensionar)
  registrarColheitaDeContextoDeTela(colherTelaHospedeira)
})

onBeforeUnmount(() => {
  pararDeObservarSuporte?.()
  window.removeEventListener('resize', aoRedimensionar)
  registrarColheitaDeContextoDeTela(null)
})
</script>

<style scoped>
.assistente-dock {
  --assistente-recuo-suporte: var(--espacamento-0);
  --assistente-respiro: var(--espacamento-12);
  min-width: 0;
  min-height: 0;
  color: var(--cor-texto-principal);
  font-family: var(--texto-tipografia);
}

.assistente-dock--com-suporte {
  --assistente-recuo-suporte: calc(var(--espacamento-56) + var(--espacamento-12));
}

.assistente-dock--oculto {
  display: none;
}

.assistente-dock--minimizado {
  display: grid;
  grid-area: assistente;
  align-content: start;
  justify-items: center;
}

.assistente-dock__acionador,
.assistente-dock__fechar-pergunta {
  border: var(--espessura-10) solid var(--cor-neutra-20);
  color: var(--cor-secundaria-100);
  background: var(--cor-background-fundo-principal);
  cursor: pointer;
}

.assistente-dock__acionador {
  display: flex;
  position: relative;
  align-items: center;
  height: var(--espacamento-56);
  padding: 0;
  border: 0;
  border-radius: var(--arredondamento-max);
  box-shadow: 0 var(--espacamento-4) var(--espacamento-16) color-mix(in srgb, var(--cor-secundaria-300) 12%, transparent);
}

.assistente-dock__foto {
  position: relative;
  flex: none;
  width: var(--espacamento-56);
  height: var(--espacamento-56);
}

.assistente-dock__foto img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--arredondamento-max);
}

.assistente-dock__selo {
  position: absolute;
  right: calc(-1 * var(--espacamento-2));
  bottom: calc(-1 * var(--espacamento-2));
  display: grid;
  place-items: center;
  box-sizing: border-box;
  width: var(--espacamento-24);
  height: var(--espacamento-24);
  border: var(--espessura-20) solid var(--cor-background-fundo-principal);
  border-radius: var(--arredondamento-max);
  color: var(--cor-destaque-contraste);
  background: var(--cor-destaque-100);
}

.assistente-dock__estrela {
  display: inline-block;
  flex: none;
  width: var(--espacamento-16);
  height: var(--espacamento-16);
  background: currentColor;
  mask: url('../../../../../../../static/icones/sparkles.svg') center / contain no-repeat;
}

.assistente-dock__rotulo {
  display: grid;
  grid-template-columns: 0fr;
  opacity: 0;
  transition: grid-template-columns .2s ease, opacity .2s ease;
}

.assistente-dock__acionador:hover .assistente-dock__rotulo,
.assistente-dock__acionador:focus-visible .assistente-dock__rotulo {
  grid-template-columns: 1fr;
  opacity: 1;
}

.assistente-dock__acionador-texto {
  overflow: hidden;
  color: var(--cor-texto-principal);
  font-family: var(--texto-tipografia);
  font-size: var(--texto-titulo-p-tamanho);
  font-weight: var(--texto-titulo-p-peso);
  line-height: var(--texto-titulo-p-entrelinha);
  white-space: nowrap;
}

.assistente-dock__acionador:hover .assistente-dock__acionador-texto,
.assistente-dock__acionador:focus-visible .assistente-dock__acionador-texto {
  margin: 0 var(--espacamento-12) 0 var(--espacamento-16);
}

@media (prefers-reduced-motion: reduce) {
  .assistente-dock__rotulo { transition: none; }
}

.assistente-dock--recolhido {
  position: fixed;
  right: var(--espacamento-24);
  bottom: calc(var(--espacamento-24) + var(--assistente-recuo-suporte));
  z-index: 1000000;
}

.assistente-dock__pergunta {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
  width: min(320px, calc(100vw - 2 * var(--espacamento-24)));
  max-width: 320px;
  height: var(--espacamento-48);
  max-height: 48px;
  padding: var(--espacamento-4);
  border: var(--espessura-10) solid var(--cor-neutra-20);
  border-radius: var(--arredondamento-8);
  background: var(--cor-background-fundo-principal);
  box-shadow: 0 10px 30px color-mix(in srgb, var(--cor-texto-principal) 18%, transparent);
}

.assistente-dock__pergunta input {
  min-width: 0;
  height: var(--espacamento-38);
  padding: 0 var(--espacamento-8);
  border: 0;
  color: var(--cor-texto-principal);
  background: transparent;
  font: inherit;
  outline: 0;
}

.assistente-dock__pergunta button {
  display: grid;
  place-items: center;
  width: var(--espacamento-32);
  height: var(--espacamento-32);
  border: 0;
  color: var(--cor-secundaria-100);
  background: transparent;
  cursor: pointer;
}

.assistente-dock__pergunta button:disabled {
  cursor: not-allowed;
  opacity: .45;
}

.assistente-dock__expandir {
  font-size: 15px;
}

.assistente-dock--acoplado {
  position: relative;
  display: grid;
  grid-area: assistente;
  grid-template-rows: minmax(0, 1fr);
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-left: var(--espessura-10) solid var(--cor-neutra-20);
  background: var(--cor-background-fundo-principal);
}

.assistente-dock--sobreposto {
  position: fixed;
  inset: 0 0 0 auto;
  z-index: 1000000;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  width: min(370px, 100vw);
  height: 100dvh;
  background: var(--cor-background-fundo-principal);
  box-shadow: -16px 0 40px color-mix(in srgb, var(--cor-texto-principal) 20%, transparent);
}

/* Em celular e tablet, conversa e resultado compartilham a largura inteira. */
@media (max-width: 1023px) {
  .assistente-dock--sobreposto {
    width: 100vw;
  }
}

.assistente-dock__painel {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.assistente-dock__painel--abas {
  grid-template-rows: var(--espacamento-44) var(--espacamento-48) minmax(0, 1fr);
}

.assistente-dock__cabecalho {
  background: var(--cor-background-fundo-principal);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--espacamento-8);
  min-height: var(--espacamento-44);
  box-sizing: border-box;
  padding: 0 var(--assistente-respiro);
  border-bottom: var(--espessura-10) solid var(--cor-neutra-20);
  font-size: var(--texto-subtitulo-p-tamanho);
  font-weight: var(--texto-subtitulo-p-peso);
  line-height: var(--texto-subtitulo-p-entrelinha);
}

.assistente-dock__identidade {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: var(--espacamento-6);
}

.assistente-dock__titulo {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assistente-dock__identidade .assistente-dock__estrela,
.assistente-dock__expandir .assistente-dock__estrela {
  color: var(--cor-destaque-100);
}

.assistente-dock__acoes {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--espacamento-4);
}

.assistente-dock__conteudo {
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

/*
 * O dock já se apresenta pelo próprio cabeçalho: título e fechar. Repetir
 * dentro dele a identidade da conversa custaria metade da altura útil da
 * coluna, então aqui sobra da conversa o que o dock não diz — a saída para
 * uma nova conversa e a tela de onde ela partiu, lado a lado numa linha só.
 */
.assistente-dock__conteudo :deep(.conversa__identidade),
.assistente-dock__conteudo :deep(.conversa__beta),
/*
 * O botão de TEXTO "+ Nova conversa" sai do dock porque a mesma ação virou
 * ícone no cabeçalho daqui, ao lado do histórico. Oferecer as duas seria a
 * mesma porta duas vezes na mesma coluna estreita. Ele continua vivo na
 * página cheia, que não tem este cabeçalho.
 */
.assistente-dock__conteudo :deep(.conversa__nova) {
  display: none;
}

.assistente-dock__conteudo :deep(.conversa__cabecalho) {
  min-height: 0;
  padding: 8px var(--assistente-respiro);
}

.assistente-dock__conteudo :deep(.conversa__origem) {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.assistente-dock__conteudo :deep(.conversa) {
  min-width: 0;
  min-height: 0;
}

.assistente-dock__anuncio {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.assistente-dock button:focus-visible,
.assistente-dock input:focus-visible {
  outline: var(--espessura-20) solid var(--cor-secundaria-100);
  outline-offset: var(--espacamento-2);
}
</style>
